import React, { useState, useEffect } from "react"
import types from "prop-types"
import { useDispatch } from "react-redux"
import { addForm } from "reduxSlices/formIndexSlice"
import { useInterval } from "hooks"
import * as API from "services/api"
import { processingDone, processingFailed } from "utils/processingHelpers"
import { activityHubFormEditPath } from "utils/routeHelpers"
import LoadingIndicator from "shared/LoadingIndicator"
import FieldLabel from "shared/FieldLabel"
import Modal, { ModalHeader } from "shared/Modal"
import { Button, PrimaryButton } from "shared/buttons"
import { CategorySelect } from "shared/selects"
import { successToast, errorToast } from "shared/toast"

const POLLING_INTERVAL = 2000

const VIEW_SELECTING = "selecting"
const VIEW_PROCESSING = "processing"
const VIEW_DONE = "done"
const VIEW_FAILED = "failed"

const CopyFormModal = ({
  formCategoryId, formSlug, isOpen, onClose,
}) => {
  const dispatch = useDispatch()
  const [view, setView] = useState(VIEW_SELECTING)
  const [categoryOptions, setCategoryOptions] = useState([])
  const [targetCategoryId, setTargetCategoryId] = useState(null)
  const [requestId, setRequestId] = useState(null)
  const [newFormSlug, setNewFormSlug] = useState(null)

  useEffect(() => {
    if (!isOpen) return

    API.getBuildableCategories().then((response) => {
      if (response.ok) setCategoryOptions(response.data)
    })
  }, [isOpen])

  const handleClose = () => {
    setView(VIEW_SELECTING)
    setTargetCategoryId(null)
    setRequestId(null)
    setNewFormSlug(null)
    onClose()
  }

  const handleSubmit = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm("Are you sure you want to copy this form?  This cannot be undone.")) {
      const response = await API.createCopyFormWithinOrganizationRequest({
        baseFormSlug: formSlug,
        targetCategoryId,
      })

      if (response.ok) {
        setRequestId(response.data.id)
        setView(VIEW_PROCESSING)
      } else {
        errorToast("Something went wrong.  Requesting form copy failed.")
        setView(VIEW_FAILED)
      }
    }
  }

  useInterval(async () => {
    const response = await API.copyFormWithinOrganizationRequest({ requestId })

    if (!response.ok) {
      errorToast("Something went wrong.  Copying the form failed.")
      setView(VIEW_FAILED)
      return
    }

    const { data: copyRequest } = response
    const { newFormSlug: slug } = copyRequest

    if (processingDone({ processable: copyRequest })) {
      const formResponse = await API.formCard({ formSlug: slug })

      if (formResponse.ok) dispatch(addForm(formResponse.data))

      successToast("Form copied successfully.")
      setNewFormSlug(slug)
      setView(VIEW_DONE)
    } else if (processingFailed({ processable: copyRequest })) {
      errorToast("Something went wrong.  Copying the form failed.")
      setView(VIEW_FAILED)
    }
  }, view === VIEW_PROCESSING ? POLLING_INTERVAL : null)

  return (
    <Modal isOpen={isOpen} aria={{ label: "copy form" }}>
      <ModalHeader closeModal={handleClose} heading="Copy Form" />
      <div>
        {
          view === VIEW_SELECTING && (
            <>
              <FieldLabel className="!max-w-full" text="Target Solution">
                <CategorySelect
                  isFullWidth
                  options={categoryOptions}
                  value={targetCategoryId}
                  onChange={setTargetCategoryId}
                  placeholder="Select a solution ..."
                />
              </FieldLabel>
              {
                targetCategoryId && targetCategoryId !== formCategoryId && (
                  <p className="text-xs text-dark">
                    The form will be copied to a different solution.
                    Reportable fields and workflows will not be included in the copy.
                  </p>
                )
              }
              <div className="flex justify-center mt-8 space-x-2">
                <Button
                  className="border border-light-300"
                  onClick={handleClose}
                  text="Cancel"
                />
                <PrimaryButton
                  disabled={!targetCategoryId}
                  onClick={handleSubmit}
                  text="Copy"
                />
              </div>
            </>
          )
        }
        {
          view === VIEW_PROCESSING && (
            <LoadingIndicator textValue="Copying form ..." />
          )
        }
        {
          view === VIEW_DONE && (
            <div className="text-center">
              <a
                className="link-text"
                href={activityHubFormEditPath(newFormSlug)}
              >
                Edit the new form
              </a>
              <div className="mt-6">
                <Button
                  className="border border-light-300"
                  onClick={handleClose}
                  text="Close"
                />
              </div>
            </div>
          )
        }
        {
          view === VIEW_FAILED && (
            <div className="text-center">
              <p className="mb-6">Something went wrong. Please try again.</p>
              <Button
                className="border border-light-300"
                onClick={handleClose}
                text="Close"
              />
            </div>
          )
        }
      </div>
    </Modal>
  )
}

CopyFormModal.propTypes = {
  formCategoryId: types.number.isRequired,
  formSlug: types.string.isRequired,
  isOpen: types.bool.isRequired,
  onClose: types.func.isRequired,
}

export default CopyFormModal
